import { ReactElement } from 'react'
import styled from 'styled-components'

import { Exclamation, Size, Spacer } from '@syconium/little-miss-figgy'
import * as AttributeSectionStyles from '@syconium/little-miss-figgy/dist/components/AttributeSection/styles'

import { usePrettySizeName } from '../../../app/_hooks/usePrettySizeName'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { SizeChart } from '../../../components/pages/products/ProductPage/DetailsSection/SizesSection/SizeChart'
import { trackEvent } from '../../../lib/analytics'
import { AnalyticsContext, ProductColorGroupWithUnavailable } from '../../../types/figs'
import { ProductColor } from '../../../types/graphql'

import {
  ColorSection,
  ColorSelector,
  ErrorMessageWrapper,
  FitSection,
  FitSelector,
  SizeChartButton,
  SizeSection,
  SizeSectionTopText,
  SizeSelector,
  SoldOutMessage,
} from './styles'

const FitSelection = styled(AttributeSectionStyles.Selection)`
  text-transform: capitalize;
`

/**
 * @deprecated Compose your precise usecase with VariantSelectionForm's exports which are composable and based on catalog-api rather than Zidi
 */
export const VariantAttributeSelectors = ({
  analyticsContext,
  availableFitsKeys,
  category,
  colorGroups,
  currentFitKey,
  currentSize,
  fitNames,
  isActiveProductWaitlistable,
  isCurrentColorCompletelySoldOut,
  openSizeChartModal,
  productGroupTitle,
  selectedColor,
  setSelectedColor,
  setSelectedFitKey,
  setSelectedSize,
  showColorSelector = true,
  sizeChart,
  sizes,
  sizeSectionTitle,
  userHasAttemptedInvalidAddToBag,
  productId,
}: {
  analyticsContext: AnalyticsContext
  availableFitsKeys: readonly string[]
  category?: string
  colorGroups: ProductColorGroupWithUnavailable[]
  currentFitKey: string
  currentSize: Size | undefined
  fitNames?: ReadonlyMap<string, string>
  isActiveProductWaitlistable: boolean
  isCurrentColorCompletelySoldOut: boolean
  openSizeChartModal(): void
  productGroupTitle: string
  selectedColor: ProductColor | undefined
  setSelectedColor(color: ProductColor): void
  setSelectedFitKey(fit: string): void
  setSelectedSize(size: Size): void
  showColorSelector?: boolean
  sizeChart?: SizeChart
  sizes: readonly Size[]
  sizeSectionTitle: string
  userHasAttemptedInvalidAddToBag: boolean
  productId: string
}): ReactElement => {
  const {
    selectAndAddVariant: selectAndAddVariantStrings,
    pages: { product: productPageStrings },
    sizesSection: { sizeSoldOutErrorMessage, sizeNotSelectedErrorMessage },
  } = useTranslation()
  /*
     const { useExtendedFixedWidthSizeSelectorItems } = useStackedColorsAndSizes(category) // <-- original

     HACK: we originally relied on the category to determine if the size buttons should be fixed-width,
     but because that string is translated for i18n we can't rely on it any longer (until we refactor).

     For now, we're going to simply check if the sizes contain a `/` which indicates sizes that we typically use fixed-width values.
  */
  const useLargeWidthSizeSelectorItems = Boolean(sizes[0]?.shorthand.includes('/'))

  const fitSectionLabel =
    category === 'Scrub Top'
      ? productPageStrings.scrubTopsFitTitle
      : productPageStrings.scrubPantsFitTitle
  const selectedFitName = fitNames?.get(currentFitKey) ?? currentFitKey

  const prettySizeName = usePrettySizeName({
    id: currentSize?.shorthand,
    displayValue: currentSize?.shorthand,
  })

  return (
    <>
      <ColorSection showColorSelector={showColorSelector}>
        <ColorSelector
          colorGroups={colorGroups}
          isCurrentColorCompletelySoldOut={isCurrentColorCompletelySoldOut}
          hasSelectedSize={!!currentSize}
          isProductWaitlistable={isActiveProductWaitlistable}
          onColorSelect={setSelectedColor}
          selectedColorKey={selectedColor?.rawName}
          {...trackEvent({
            category: `${analyticsContext.category}`,
            action: 'select color',
            label: 'modal color',
          })}
        />
      </ColorSection>

      <SizeSection
        className={`size-selector-${productId}`}
        data-has-error={userHasAttemptedInvalidAddToBag}
        tabIndex={-1}
      >
        <SizeSectionTopText>
          <div>
            <AttributeSectionStyles.Title>
              <AttributeSectionStyles.Label>{sizeSectionTitle}</AttributeSectionStyles.Label>
              <AttributeSectionStyles.Selection>
                {prettySizeName}
                {currentSize?.soldOut && !isActiveProductWaitlistable ? (
                  <SoldOutMessage>&nbsp;{selectAndAddVariantStrings.soldOutMessage}</SoldOutMessage>
                ) : null}
              </AttributeSectionStyles.Selection>
            </AttributeSectionStyles.Title>
          </div>
          {sizeChart && (
            <SizeChartButton
              as='button'
              onClick={openSizeChartModal}
              {...trackEvent({
                category: `${analyticsContext.category} size chart`,
                action: 'click',
                label: 'modal size chart',
              })}
            >
              {selectAndAddVariantStrings.sizeSection.sizeChartCta}
            </SizeChartButton>
          )}
        </SizeSectionTopText>
        <SizeSelector
          sizes={sizes}
          context={productGroupTitle}
          minWidthStyle={useLargeWidthSizeSelectorItems ? 'lg' : 'md'}
          onSelectSize={setSelectedSize}
          selected={currentSize}
          disableMobileHorizontalSwipe
          {...trackEvent({
            category: `${analyticsContext.category}`,
            action: 'select size',
            label: 'modal size',
          })}
        />
        {userHasAttemptedInvalidAddToBag && (
          <ErrorMessageWrapper>
            <Exclamation />
            <div role='alert' aria-live='polite'>
              {currentSize?.soldOut ? sizeSoldOutErrorMessage : sizeNotSelectedErrorMessage}
            </div>
          </ErrorMessageWrapper>
        )}
      </SizeSection>

      {availableFitsKeys.length > 1 ? (
        <FitSection>
          <div>
            <AttributeSectionStyles.Title>
              <AttributeSectionStyles.Label>{fitSectionLabel}</AttributeSectionStyles.Label>
              <FitSelection>{selectedFitName}</FitSelection>
            </AttributeSectionStyles.Title>
          </div>
          <FitSelector
            context={productGroupTitle}
            fits={availableFitsKeys}
            fitNames={fitNames}
            onFitSelect={setSelectedFitKey}
            selected={currentFitKey}
            disableMobileHorizontalSwipe
            {...trackEvent({
              category: `${analyticsContext.category}`,
              action: 'select fit',
              label: 'modal fit',
            })}
          />
        </FitSection>
      ) : (
        <Spacer height={8} />
      )}
    </>
  )
}
